import { List, OrderedMap } from 'immutable';
import { createSelector } from 'reselect';

import { accountsSelectors, accountsUtils } from '../accounts';
import { businessSelectors } from '../business';
import { balanceSelectors } from '../calculated-balances';
import { featureFlagsSelectors } from '../feature-flags';
import { investmentHoldingsV2Selectors } from '../investment-holdings-v2';

import { preferencesV2Selectors } from '../preferences-v2';
import { AccountNode } from './accountNodeTypes';
import { getL1GroupingData, getL2GroupingData, hasL2Grouping, l2Substrings } from './accountNodeUtils';

function balRound(val) {
  const ret = val ? Number(val.toFixed(2)) : 0;
  return ret || 0;
}

function hasHiddenChild(node) {
  return node.children && node.children.find((x) => x.account.isExcludedFromAccountBar);
}

const accountIdsCache = {};
function memoizedAccountIdList(ids) {
  const normalizedIds = ids.sort((a, b) => a > b);
  const key = JSON.stringify(normalizedIds);
  if (!accountIdsCache[key]) {
    accountIdsCache[key] = List(ids);
  }
  return accountIdsCache[key];
}

const findOrCreateNodeForGroup = (list, data, depth) => {
  let foundNode = list.find((node) => node.id === data.id);
  if (!foundNode) {
    foundNode = { ...data, depth, children: [] };
    list.push(foundNode);
  }
  return foundNode;
};

const getAllNodeForAccounts = (accountsById) => {
  const allNodes = {
    id: 'all',
    displayLabel: 'Net Worth',
    routeTo: '/transactions',
    displayOrder: 0,
    expandable: false,
    depth: 1,
    children: [],
  };
  accountsById.forEach((account) => {
    if (account.type !== 'GOAL') {
      allNodes.children.push({
        id: account.id || account.clientId,
        depth: 2,
        displayLabel: assert(account.name, 'name not defined'),
        routeTo: '/transactions',
        account,
      });
    }
  });

  // order allNodes (net-worth) children based on grouping data too
  allNodes.children.sort((a, b) => {
    let compareVal = getL1GroupingData(a.account).displayOrder - getL1GroupingData(b.account).displayOrder;
    if (compareVal === 0) {
      compareVal = getL2GroupingData(a, a.account).displayOrder - getL2GroupingData(b, b.account).displayOrder;
    }
    return compareVal;
  });

  return allNodes;
};

const getAccountNodesTreeForAccounts = (accounts) => {
  // reduce accounts into L1 groupings
  let l1Nodes = accounts.reduce((list, account) => {
    const l1Group = findOrCreateNodeForGroup(list, getL1GroupingData(account), 1);
    l1Group.children.push({
      id: account.id || account.clientId,
      depth: 2,
      displayLabel: assert(account.name, 'name not defined'),
      account,
      balanceType: l1Group.balanceType,
    });
    return list;
  }, []);

  // after creating all l1Nodes, sort by display order
  l1Nodes.sort((a, b) => a.displayOrder - b.displayOrder);

  // expand children in L1 groupings into L2 groupings (if required) and perform sorting
  l1Nodes.forEach((node) => {
    if (hasL2Grouping(node.id, node.children.length) || hasHiddenChild(node)) {
      node.children = node.children.reduce((list, child) => {
        const l2Group = findOrCreateNodeForGroup(list, getL2GroupingData(node, child.account), 2);
        l2Group.children.push({
          id: child.account.id || child.account.clientId,
          depth: 3,
          displayLabel: assert(child.account.name, 'name not defined'),
          routeTo: l2Group.routeTo,
          account: child.account,
          balanceType: l2Group.balanceType,
        });

        return list;
      }, []);
      node.children.sort((a, b) => a.displayOrder - b.displayOrder);
      node.children.forEach((l3Node) => {
        l3Node.children.sort((a, b) =>
          a.displayLabel?.localeCompare(b.displayLabel, undefined, { numeric: true, sensitivity: 'base' }),
        );
      });
    } else {
      node.children.sort((a, b) =>
        a.displayLabel.localeCompare(b.displayLabel, undefined, { numeric: true, sensitivity: 'base' }),
      );
      node.children.forEach((l3Node) => {
        l3Node.routeTo = node.routeTo;
        l3Node.isLeaf = true;
      });
    }
  });

  return List(l1Nodes);
};

// expects props: (state, balanceType)
export const getAccountNodesTree = createSelector(
  accountsSelectors.getAccountsById,
  balanceSelectors.getBalancesByAccountIds,
  businessSelectors.getResourcesById,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentTransactions'), // need it to force the refresh
  (state) => preferencesV2Selectors.getPreferenceValue(state, 'hideClosedAccBar'),
  (state) => preferencesV2Selectors.getPreferenceValue(state, 'hideGoalsAccBar'),
  (state) => {
    if (featureFlagsSelectors.getFeatureFlag(state, 'reorderAccountBar')) {
      return preferencesV2Selectors.getPreferenceValue(state, 'sAccountBarOrder');
    }
    return undefined;
  },
  (state) => investmentHoldingsV2Selectors.getHoldingsTotalsByAccountId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (_state, params) => params?.ignoreUsageType ?? false,
  (
    accountsById,
    balancesById,
    businessesById,
    _investmentTransactions,
    hideClosed,
    hideGoals,
    accountBarOrder,
    investTotals,
    securitiesFF,
    ignoreUsageType,
  ) => {
    let rootNodes = [];

    const allNodes = getAllNodeForAccounts(accountsById);
    rootNodes.push(allNodes);

    const filteredAccounts = accountsById.filter(
      (account) => account.type !== 'GOAL' && (!hideClosed || !account.isClosed),
    );
    const accountByUsages = filteredAccounts.reduce((map, account) => {
      const usage = account.businessId || 'PERSONAL';
      if (!map.has(usage)) {
        map.set(usage, []);
      }
      map.get(usage).push(account);
      return map;
    }, new Map());

    // if (usages.length === 1) get L1+ Nodes and add to root-nodes
    // if (usages.length > 1) build L0 node, add to root-nodes and add L1+ nodes to L0 node for account-ids

    if (accountByUsages.size === 1 || ignoreUsageType) {
      const l1Nodes = getAccountNodesTreeForAccounts(filteredAccounts);
      rootNodes.push(...l1Nodes);
    } else {
      accountByUsages.forEach((accounts, usage) => {
        let displayLabel = 'Personal';
        let displayThemeBgColor = 'background.layer1';
        let displayThemeColor;
        if (usage !== 'PERSONAL') {
          const business = businessesById.get(usage);
          displayLabel = business?.name || '';
          displayThemeBgColor = business?.color ? `${business.color}.light` : 'background.layer1';
          displayThemeColor = business?.color ? `${business.color}.dark` : 'text.primary';
        }
        const l0Node = findOrCreateNodeForGroup(
          rootNodes,
          {
            id: usage,
            displayLabel,
            displayThemeBgColor,
            displayThemeColor,
            expandable: true,
            routeTo: '/transactions',
          },
          1,
        );
        l0Node.children = getAccountNodesTreeForAccounts(accounts);
      });
      rootNodes.sort((a, b) => {
        // Special cases: if id is "all", it should be first
        if (a.id === 'all') return -1;
        if (b.id === 'all') return 1;

        // Any nodes in the accountBarOrder pref are ordered using the pref
        if (accountBarOrder) {
          const order = (accountBarOrder[a.id] ?? Infinity) - (accountBarOrder[b.id] ?? Infinity);
          if (order) {
            return order;
          }
        }

        // Special cases: if displayLabel is "Personal", it should be last
        if (a.displayLabel === 'Personal') return 1;
        if (b.displayLabel === 'Personal') return -1;

        // otherwise, sort by displayLabel (business-name)
        return a.displayLabel.localeCompare(b.displayLabel, undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    const rollupAccountNodes = (node, securitiesFF) => {
      /* eslint-disable no-param-reassign */
      let allAccountIds = [];
      let balance = 0;
      let goalsBalance = 0;
      let currency = null;

      if (node.id !== 'GOALS' && node.children) {
        node.children = node.children.map((child) => {
          const childAfterRollup = rollupAccountNodes(child, securitiesFF);
          allAccountIds = allAccountIds.concat(childAfterRollup.allAccountIds.toArray());
          balance += childAfterRollup.balance;
          if (!hideGoals) {
            goalsBalance += childAfterRollup.goalsBalance;
          }

          if (!currency) {
            currency = childAfterRollup.currency;
          } else if (currency !== childAfterRollup.currency) {
            currency = 'N/A';
          }

          return childAfterRollup;
        });
        node.children = List(node.children);
      }

      if (node.account) {
        allAccountIds.push(node.account.id);

        const balancesForAccount = balancesById.get(node.account.id);
        if (balancesForAccount) {
          balance += balancesForAccount.balance;
          if (!hideGoals) {
            goalsBalance += balancesForAccount.goalsBalance;
          }
        }

        if (!currency) {
          currency = node.account.currency;
        } else if (currency !== node.account.currency) {
          currency = 'N/A';
        }
      }

      if (accountsUtils.isAccountManual(node.account) && node?.account?.type === 'INVESTMENT' && securitiesFF) {
        balance = investTotals.get(node.account.id);
      } else {
        balance = balRound(balance);
      }
      goalsBalance = hideGoals ? 0 : balRound(goalsBalance);

      node.allAccountIds = memoizedAccountIdList(allAccountIds);
      node.balance = balance;
      node.goalsBalance = goalsBalance;
      node.availableBalance = balance - goalsBalance;
      node.currency = currency;
      node.hide = node.account ? node.account.isExcludedFromAccountBar : false;

      return AccountNode(node);
    };

    rootNodes = rootNodes.map((node) => rollupAccountNodes(node, securitiesFF));

    // special handling for investment accounts: let code above to include investment account balances,
    // but exclude all investment account transactions from all/networth node
    rootNodes = rootNodes.map((node) =>
      node.id !== 'all'
        ? node
        : node.merge({
            allAccountIds: node.allAccountIds.filter((accountId) => accountsById.get(accountId)?.type !== 'INVESTMENT'),
          }),
    );

    return List(rootNodes);
  },
);

// other stuff that I copied over (probably outdated)
// - - - - - - - - - - - - - - - - - - -

const getAccountsByIdForNode = (node) => {
  let flattenedList = [[node.id, node]];
  if (node.children) {
    flattenedList = node.children.reduce((list, child) => list.concat(getAccountsByIdForNode(child)), flattenedList);
  }
  return flattenedList;
};

export const getAccountNodesById = createSelector(getAccountNodesTree, (tree) => {
  if (!tree || tree.size === 0) {
    return OrderedMap();
  }
  return OrderedMap(tree.reduce((list, node) => list.concat(getAccountsByIdForNode(node)), []));
});

export const getAccountNodeById = createSelector(
  getAccountNodesById,
  (state, id) => id,
  (accountNodes, id) => accountNodes.get(id),
);

const accountIdToDisplayLabel = {
  BANKING: 'Banking',
  LIABILITY: 'Liabilities',
  ASSET: 'Assets',
  INVESTMENT: 'Investments',
};

export const getAccountsToShowAddOptionUnder = createSelector(getAccountNodesById, (accountNodesById) => {
  return l2Substrings
    .filter((accountOption) => !accountNodesById.some((accountNode) => accountNode.id.includes(accountOption)))
    .map((id) => accountIdToDisplayLabel[id]);
});

const sortByName = (a, b) => {
  const accA = a?.name?.toLowerCase();
  const accB = b?.name?.toLowerCase();
  if (accA > accB) {
    return 1;
  }
  if (accA < accB) return -1;
  return 0;
};

const sortByOrder = (a, b) => {
  const accA = getL2GroupingData(null, a.first())?.displayOrder;
  const accB = getL2GroupingData(null, b.first())?.displayOrder;
  if (accA > accB) {
    return 1;
  }
  if (accA < accB) return -1;
  return 0;
};

// this selector returns the accounts used in the upcoming cash flow graph legend
export const getAccountIdsForProjected = createSelector(accountsSelectors.getAccountsById, (accountsById) => {
  const accounts = accountsById.filter((acct) => acct.type !== 'INVESTMENT').sort(sortByName);
  return {
    groupedAccounts: accounts
      .toList()
      .groupBy((acc) => getL2GroupingData(null, acc)?.displayLabel)
      .sort(sortByOrder),
    accountIds: List([...accounts.keys()]),
  };
});

/** Used to select data for l0 (personal, biz1, biz2...) account reorder feature */
export const getAccountReorderNodes = createSelector(getAccountNodesTree, (accountNodesTree) => {
  const topLevelNodes = accountNodesTree.slice(1);

  // Determine if the topLevelNodes are l0 or l1
  const hasl0 = topLevelNodes.find((node) => node.depth === 1 && node.displayOrder === 0);
  if (!hasl0) {
    return [
      {
        businessId: 'PERSONAL',
        displayThemeColor: 'background.layer1',
        displayThemeBgColor: 'text.primary',
        displayLabel: 'Personal',
      },
    ];
  }

  return topLevelNodes.toArray().map((l0Node) => ({
    businessId: l0Node.id,
    displayThemeColor: l0Node.displayThemeColor,
    displayThemeBgColor: l0Node.displayThemeBgColor,
    displayLabel: l0Node.displayLabel,
  }));
});
